import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, apiRoutes } from '../../api';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import '@fontsource/vazirmatn';
import './ListingProfile.css';

const WEEKDAYS = ['دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه', 'یکشنبه'];

const FURNISHED_LABELS = {
    UNFURNISHED: 'بدون مبله',
    SEMI_FURNISHED: 'نیمه مبله',
    FULLY_FURNISHED: 'کاملاً مبله',
};

const MORTGAGE_LABELS = {
    NONE: 'بدون وام',
    AVAILABLE: 'وام دارد',
};

function Section({ title, children }) {
    return (
        <div className="lp-section">
            <h2 className="lp-section-title">{title}</h2>
            <div className="lp-section-body">{children}</div>
        </div>
    );
}

function Row({ label, value }) {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="lp-row">
            <span className="lp-label">{label}</span>
            <span className="lp-value">{value}</span>
        </div>
    );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Converts visiting_schedules from the API into FullCalendar businessHours format.
 * Backend weekday: 0=Mon … 6=Sun  →  FullCalendar: 0=Sun … 6=Sat
 */
function schedulesToBusinessHours(schedules) {
    if (!schedules || schedules.length === 0) return [];
    return schedules.map((s) => {
        const entry = { startTime: s.start_time, endTime: s.end_time };
        if (s.weekday != null) {
            entry.daysOfWeek = [(s.weekday + 1) % 7];
        } else {
            // date-specific: show only on that exact day
            const d = new Date(s.date);
            entry.daysOfWeek = [d.getDay()];
            entry.startRecur = s.date;
            entry.endRecur = new Date(d.getTime() + 86400000).toISOString().slice(0, 10);
        }
        return entry;
    });
}

// ── Status badge labels ──────────────────────────────────────────────────────
const SHOWING_STATUS_LABELS = {
    REQUESTED: { text: 'در انتظار تأیید نماینده', color: '#f57c00', bg: '#fff3e0' },
    CONFIRMED: { text: 'بازدید تأیید شد', color: '#1565c0', bg: '#e3f2fd' },
    REJECTED:  { text: 'درخواست رد شد', color: '#c62828', bg: '#ffebee' },
    CANCELED:  { text: 'درخواست لغو شد', color: '#757575', bg: '#f5f5f5' },
    EXPIRED:   { text: 'زمان درخواست منقضی شد', color: '#757575', bg: '#f5f5f5' },
    COMPLETED: { text: 'شما از این آگهی بازدید کرده‌اید', color: '#2e7d32', bg: '#e8f5e9' },
};

// ── Survey sub-component ─────────────────────────────────────────────────────
function SurveyForm({ showingId, existingRating, existingComment, token, onDone }) {
    const [rating, setRating] = useState(existingRating || 0);
    const [comment, setComment] = useState(existingComment || '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const alreadyFilled = existingRating != null;

    const handleSubmit = async () => {
        if (!rating) { setError('لطفاً امتیاز خود را انتخاب کنید.'); return; }
        setSubmitting(true);
        setError(null);
        try {
            await api.post(
                apiRoutes.users.showingSurvey(showingId),
                { rating, comment },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            onDone(rating, comment);
        } catch (err) {
            const data = err.response?.data;
            setError(typeof data === 'object' ? Object.values(data).flat().join(' ') : 'خطا در ثبت نظر');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="lp-survey-form">
            <p className="lp-showing-hint">نظر خود را درباره این بازدید ثبت کنید:</p>
            <div className="lp-survey-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        className={`lp-star-btn ${n <= rating ? 'active' : ''}`}
                        onClick={() => !alreadyFilled && setRating(n)}
                        disabled={alreadyFilled}
                    >
                        ★
                    </button>
                ))}
            </div>
            <textarea
                className="lp-showing-input lp-showing-notes"
                placeholder="توضیحات (اختیاری)"
                value={comment}
                onChange={(e) => !alreadyFilled && setComment(e.target.value)}
                rows={2}
                disabled={alreadyFilled}
            />
            {error && <div className="lp-showing-error">{error}</div>}
            {!alreadyFilled && (
                <button
                    className="lp-showing-btn"
                    onClick={handleSubmit}
                    disabled={submitting || !rating}
                    type="button"
                >
                    {submitting ? 'در حال ثبت...' : 'ثبت نظر'}
                </button>
            )}
            {alreadyFilled && (
                <p className="lp-showing-hint" style={{ color: '#2e7d32' }}>✓ نظر شما ثبت شده است.</p>
            )}
        </div>
    );
}

// ── Offer widget (buyer submits price offer during negotiation) ──────────────
function OfferWidget({ reservationId, listingType, token }) {
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        asking_price: '',
        deposit: '',
        monthly_rent: '',
        payment_phases: [],
    });
    const [phaseInput, setPhaseInput] = useState({ label: '', date: '', amount: '' });

    const isSale = listingType === 'SALE';

    const fetchOffer = async () => {
        try {
            const res = await api.get(
                apiRoutes.dealbridge.offers,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const all = res.data?.results ?? res.data ?? [];
            const found = Array.isArray(all)
                ? all.find(o => o.reservation === reservationId)
                : null;
            setOffer(found || null);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };

    useEffect(() => { if (reservationId && token) fetchOffer(); }, [reservationId]); // eslint-disable-line

    const addPhase = () => {
        if (!phaseInput.date || !phaseInput.amount) return;
        setForm(f => ({ ...f, payment_phases: [...f.payment_phases, { ...phaseInput }] }));
        setPhaseInput({ label: '', date: '', amount: '' });
    };

    const removePhase = (i) => setForm(f => ({ ...f, payment_phases: f.payment_phases.filter((_, idx) => idx !== i) }));

    const handleSubmit = async () => {
        setError(null);
        setSubmitting(true);
        try {
            const payload = { reservation: reservationId, payment_phases: form.payment_phases };
            if (isSale) payload.asking_price = form.asking_price;
            else { payload.deposit = form.deposit; payload.monthly_rent = form.monthly_rent; }

            await api.post(
                apiRoutes.dealbridge.offers,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShowForm(false);
            setForm({ asking_price: '', deposit: '', monthly_rent: '', payment_phases: [] });
            fetchOffer();
        } catch (err) {
            const data = err.response?.data;
            setError(typeof data === 'object' ? Object.values(data).flat().join(' ') : 'خطا در ارسال پیشنهاد');
        } finally { setSubmitting(false); }
    };

    // Map new offer status values to display labels
    const OFFER_STATUS = {
        pending_response:    { text: 'در انتظار پاسخ مالک', color: '#f57c00', bg: '#fff3e0' },
        changes_requested:   { text: 'تغییرات درخواست شده', color: '#1565c0', bg: '#e3f2fd' },
        accepted:            { text: 'پیشنهاد پذیرفته شد ✓', color: '#2e7d32', bg: '#e8f5e9' },
        rejected:            { text: 'پیشنهاد رد شد',        color: '#c62828', bg: '#ffebee' },
        withdrawn:           { text: 'پیشنهاد پس گرفته شد',  color: '#757575', bg: '#f5f5f5' },
        expired:             { text: 'پیشنهاد منقضی شد',     color: '#757575', bg: '#f5f5f5' },
        cancelled:           { text: 'پیشنهاد لغو شد',       color: '#757575', bg: '#f5f5f5' },
    };

    // Current revision terms for display
    const revision = offer?.current_revision;

    if (loading) return null;

    const canSubmit = !offer || ['rejected', 'withdrawn', 'expired', 'cancelled'].includes(offer.status);

    return (
        <div style={{ marginTop: 16 }}>
            <div className="lp-reservation-divider" />
            <p className="lp-showing-hint" style={{ fontWeight: 600, marginBottom: 8 }}> پیشنهاد قیمت</p>

            {offer && (
                <div className="lp-showing-status-badge"
                    style={{ background: OFFER_STATUS[offer.status]?.bg, color: OFFER_STATUS[offer.status]?.color, marginBottom: 8 }}>
                    {OFFER_STATUS[offer.status]?.text || offer.status}
                </div>
            )}

            {/* Show current revision terms when negotiation is ongoing */}
            {revision && offer.status === 'changes_requested' && (
                <div style={{ fontSize: 13, color: '#555', marginBottom: 8, padding: '8px', background: '#f5f5f5', borderRadius: 6 }}>
                    <strong>پیشنهاد طرف مقابل:</strong>
                    {revision.asking_price && <div>قیمت: {Number(revision.asking_price).toLocaleString()} تومان</div>}
                    {revision.monthly_rent && <div>اجاره: {Number(revision.monthly_rent).toLocaleString()} تومان</div>}
                    {revision.deposit && <div>ودیعه: {Number(revision.deposit).toLocaleString()} تومان</div>}
                </div>
            )}

            {canSubmit && !showForm && (
                <button className="lp-showing-btn" onClick={() => setShowForm(true)} type="button">
                    {offer ? ' ارسال پیشنهاد جدید' : ' ارسال پیشنهاد قیمت'}
                </button>
            )}

            {showForm && (
                <div className="lp-offer-form">
                    {isSale ? (
                        <input className="lp-showing-input" type="number" placeholder="قیمت پیشنهادی (تومان)"
                            value={form.asking_price} onChange={e => setForm(f => ({ ...f, asking_price: e.target.value }))} />
                    ) : (
                        <>
                            <input className="lp-showing-input" type="number" placeholder="ودیعه پیشنهادی (تومان)"
                                value={form.deposit} onChange={e => setForm(f => ({ ...f, deposit: e.target.value }))} />
                            <input className="lp-showing-input" type="number" placeholder="اجاره ماهانه پیشنهادی (تومان)"
                                value={form.monthly_rent} onChange={e => setForm(f => ({ ...f, monthly_rent: e.target.value }))} style={{ marginTop: 6 }} />
                        </>
                    )}

                    {/* Payment phases */}
                    <p className="lp-showing-hint" style={{ marginTop: 10, marginBottom: 4 }}>مراحل پرداخت (اختیاری):</p>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <input className="lp-showing-input" style={{ flex: 1, minWidth: 80 }} placeholder="عنوان" value={phaseInput.label}
                            onChange={e => setPhaseInput(p => ({ ...p, label: e.target.value }))} />
                        <input className="lp-showing-input" style={{ flex: 1, minWidth: 110 }} type="date" value={phaseInput.date}
                            onChange={e => setPhaseInput(p => ({ ...p, date: e.target.value }))} />
                        <input className="lp-showing-input" style={{ flex: 1, minWidth: 100 }} type="number" placeholder="مبلغ"
                            value={phaseInput.amount} onChange={e => setPhaseInput(p => ({ ...p, amount: e.target.value }))} />
                        <button className="lp-showing-btn" style={{ padding: '4px 10px' }} onClick={addPhase} type="button">+</button>
                    </div>
                    {form.payment_phases.map((ph, i) => (
                        <div key={i} className="lp-booking-chip" style={{ marginTop: 4 }}>
                            {ph.label && `${ph.label} — `}{ph.date}: {Number(ph.amount).toLocaleString()} تومان
                            <button onClick={() => removePhase(i)} style={{ marginRight: 6, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                        </div>
                    ))}

                    {error && <div className="lp-showing-error" style={{ marginTop: 6 }}>{error}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button className="lp-showing-btn" onClick={handleSubmit} disabled={submitting} type="button">
                            {submitting ? 'در حال ارسال...' : 'ارسال پیشنهاد'}
                        </button>
                        <button className="lp-showing-btn" style={{ background: '#757575' }} onClick={() => setShowForm(false)} type="button">
                            انصراف
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Reservation button sub-component ────────────────────────────────────────
function ReservationButton({ listingId, token }) {
    // null = not checked yet, object = existing reservation, false = none
    const [existing, setExisting] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Check on mount if user already has a reservation for this listing
    useEffect(() => {
        if (!token) { setExisting(false); return; }
        api.get(apiRoutes.dealbridge.reservations, { params: { scope: 'sent' }, headers: { Authorization: `Bearer ${token}` } })
            .then((res) => {
                const results = res.data?.results ?? res.data;
                const found = Array.isArray(results)
                    ? results.find((r) => r.listing === listingId || r.listing_info?.id === listingId)
                    : null;
                setExisting(found || false);
            })
            .catch(() => setExisting(false));
    }, [listingId, token]);

    const RESERVATION_STATUS_LABELS = {
        PEND:        { text: 'درخواست رزرو ثبت شد — در انتظار پاسخ مالک', color: '#f57c00', bg: '#fff3e0' },
        NEGOTIATING: { text: 'مالک پذیرفت — در حال مذاکره', color: '#1565c0', bg: '#e3f2fd' },
        ACCEPTED:    { text: 'پیشنهاد شما پذیرفته شد', color: '#2e7d32', bg: '#e8f5e9' },
        REJECTED:    { text: 'درخواست رزرو رد شد', color: '#c62828', bg: '#ffebee' },
        COMPLETED:   { text: 'رزرو نهایی شد', color: '#2e7d32', bg: '#e8f5e9' },
        CANCELLED:   { text: 'درخواست رزرو لغو شد', color: '#757575', bg: '#f5f5f5' },
    };

    const handleReserve = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const res = await api.post(
                apiRoutes.dealbridge.reservations,
                { listing: listingId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setExisting(res.data);
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 400) {
                // Already exists — fetch and show it
                try {
                    const listRes = await api.get(apiRoutes.dealbridge.reservations, { params: { scope: 'sent' }, headers: { Authorization: `Bearer ${token}` } });
                    const results = listRes.data?.results ?? listRes.data;
                    const found = Array.isArray(results)
                        ? results.find((r) => r.listing === listingId || r.listing_info?.id === listingId)
                        : null;
                    if (found) { setExisting(found); return; }
                } catch { /* ignore */ }
            }
            setError(typeof data === 'object' ? Object.values(data).flat().join(' ') : 'خطا در ثبت درخواست');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        if (!existing?.id) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await api.post(
                apiRoutes.dealbridge.reservationAction(existing.id, 'cancel'),
                { rejection_note: '' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setExisting(res.data);
        } catch (err) {
            const data = err.response?.data;
            setError(typeof data === 'object' ? Object.values(data).flat().join(' ') : 'خطا در لغو درخواست');
        } finally {
            setSubmitting(false);
        }
    };

    // Still loading
    if (existing === null) return null;

    // Has an existing reservation — show its status
    if (existing) {
        const badge = RESERVATION_STATUS_LABELS[existing.status] || { text: existing.status, color: '#333', bg: '#eee' };
        const canCancel = !['COMPLETED', 'REJECTED', 'CANCELLED'].includes(existing.status);
        const isNegotiating = existing.status === 'NEGOTIATING';

        return (
            <div style={{ marginTop: 16 }}>
                <div className="lp-reservation-divider" />
                <div className="lp-showing-status-badge" style={{ background: badge.bg, color: badge.color }}>
                    🏠 {badge.text}
                </div>
                {error && <div className="lp-showing-error" style={{ marginTop: 6 }}>{error}</div>}

                {/* Offer widget — visible while negotiating */}
                {isNegotiating && (
                    <OfferWidget
                        reservationId={existing.id}
                        listingType={existing.listing_info?.listing_type}
                        token={token}
                    />
                )}

                {canCancel && (
                    <button
                        className="lp-showing-btn"
                        style={{ marginTop: 8, background: '#757575' }}
                        onClick={handleCancel}
                        disabled={submitting}
                        type="button"
                    >
                        {submitting ? 'در حال لغو...' : 'لغو درخواست رزرو'}
                    </button>
                )}
            </div>
        );
    }

    // No reservation yet — show the button
    return (
        <div style={{ marginTop: 16 }}>
            <div className="lp-reservation-divider" />
            <p className="lp-showing-hint" style={{ marginBottom: 8 }}>
                آیا تمایل به رزرو این ملک دارید؟
            </p>
            {error && <div className="lp-showing-error" style={{ marginBottom: 8 }}>{error}</div>}
            <button
                className="lp-showing-btn lp-reserve-btn"
                onClick={handleReserve}
                disabled={submitting}
                type="button"
            >
                {submitting ? 'در حال ارسال...' : '🏠 درخواست رزرو ملک'}
            </button>
        </div>
    );
}

// ── Main Showing Widget ──────────────────────────────────────────────────────
function ShowingRequestWidget({ listingId, schedules }) {
    const calendarRef = useRef(null);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Existing showing state
    const [loadingExisting, setLoadingExisting] = useState(true);
    const [existing, setExisting] = useState(null); // ShowingRequest object or null
    const [surveyRating, setSurveyRating] = useState(null);
    const [surveyComment, setSurveyComment] = useState(null);

    const token = localStorage.getItem('access_token');
    const businessHours = schedulesToBusinessHours(schedules);
    const hasWindows = businessHours.length > 0;

    // On mount: check if user already has a showing for this listing
    useEffect(() => {
        if (!token) { setLoadingExisting(false); return; }
        api.get(apiRoutes.users.showings, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => {
                const results = res.data?.results ?? res.data;
                const found = Array.isArray(results)
                    ? results.find((s) => s.listing === listingId || s.listing?.id === listingId)
                    : null;
                if (found) {
                    setExisting(found);
                    setSurveyRating(found.rating ?? null);
                    setSurveyComment(found.comment ?? null);
                }
            })
            .catch(() => { /* silently ignore — treat as no existing showing */ })
            .finally(() => setLoadingExisting(false));
    }, [listingId, token]);

    // ── Render: existing showing ─────────────────────────────────────────────
    if (loadingExisting) {
        return <p className="lp-showing-hint">در حال بررسی...</p>;
    }

    if (existing) {
        const s = existing.status;
        const badge = SHOWING_STATUS_LABELS[s] || { text: s, color: '#333', bg: '#eee' };
        const isCompleted = s === 'COMPLETED';

        return (
            <div className="lp-booking-widget">
                {/* Status badge */}
                <div
                    className="lp-showing-status-badge"
                    style={{ background: badge.bg, color: badge.color }}
                >
                    {isCompleted ? '✅' : '📋'} {badge.text}
                </div>

                {/* Confirmed: show scheduled time */}
                {s === 'CONFIRMED' && existing.event && (
                    <div className="lp-showing-event-info">
                        <span className="lp-label">زمان بازدید:</span>
                        <span className="lp-value" style={{ direction: 'ltr' }}>
                            {new Date(existing.event.start_at).toLocaleString('fa-IR', {
                                dateStyle: 'medium', timeStyle: 'short',
                            })}
                        </span>
                    </div>
                )}

                {/* Completed: survey + reservation */}
                {isCompleted && (
                    <>
                        <SurveyForm
                            showingId={existing.id}
                            existingRating={surveyRating}
                            existingComment={surveyComment}
                            token={token}
                            onDone={(r, c) => { setSurveyRating(r); setSurveyComment(c); }}
                        />
                        {/* Show reservation button only after survey is filled */}
                        {surveyRating != null && (
                            <ReservationButton listingId={listingId} token={token} />
                        )}
                    </>
                )}
            </div>
        );
    }

    // ── Render: new showing form ─────────────────────────────────────────────
    const calEvents = selectedSlots.map((s, i) => ({
        id: `slot-${i}`,
        title: `زمان ${i + 1}`,
        start: s.start,
        end: s.end,
        backgroundColor: '#e65100',
        borderColor: '#bf360c',
        textColor: '#fff',
    }));

    const handleSelect = (info) => {
        setError(null);
        const label = new Date(info.start).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });
        setSelectedSlots((prev) => [...prev, { start: info.startStr, end: info.endStr, label }]);
        info.view.calendar.unselect();
    };

    const removeSlot = (i) => {
        setSelectedSlots((prev) => prev.filter((_, idx) => idx !== i));
        setError(null);
    };

    const handleSubmit = async () => {
        setError(null);
        if (selectedSlots.length === 0) { setError('حداقل یک زمان از تقویم انتخاب کنید.'); return; }
        if (!token) { setError('برای درخواست بازدید ابتدا وارد شوید.'); return; }
        setSubmitting(true);
        try {
            const res = await api.post(
                apiRoutes.users.showings,
                {
                    listing: listingId,
                    offered_slots: selectedSlots.map((s) => ({ start: s.start, end: s.end })),
                    notes,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setExisting(res.data);
        } catch (err) {
            const data = err.response?.data;
            setError(typeof data === 'object' ? Object.values(data).flat().join(' ') : 'خطا در ارسال درخواست');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="lp-booking-widget">
            {hasWindows ? (
                <p className="lp-showing-hint">
                    زمان‌های سفید قابل انتخاب هستند. روی تقویم کلیک کرده یا بکشید تا زمان بازدید انتخاب کنید.
                </p>
            ) : (
                <p className="lp-showing-hint">
                    روی تقویم کلیک کرده یا بکشید تا زمان‌های پیشنهادی خود را انتخاب کنید.
                </p>
            )}

            <div className="lp-booking-calendar">
                <FullCalendar
                    ref={calendarRef}
                    plugins={[timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
                    locale="fa"
                    direction="rtl"
                    height="auto"
                    selectable
                    selectMirror
                    events={calEvents}
                    select={handleSelect}
                    slotMinTime="07:00:00"
                    slotMaxTime="22:00:00"
                    slotDuration="01:00:00"
                    slotLabelInterval="01:00:00"
                    firstDay={6}
                    allDaySlot={false}
                    nowIndicator
                    businessHours={hasWindows ? businessHours : false}
                    selectConstraint={hasWindows ? 'businessHours' : undefined}
                    buttonText={{ today: 'امروز', week: 'هفته', day: 'روز' }}
                    slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                    eventClick={(info) => {
                        const idx = parseInt(info.event.id.replace('slot-', ''), 10);
                        removeSlot(idx);
                    }}
                />
            </div>

            {selectedSlots.length > 0 && (
                <div className="lp-booking-slots">
                    <p className="lp-showing-hint">زمان‌های انتخاب‌شده (برای حذف روی هر مورد کلیک کنید):</p>
                    <div className="lp-booking-chips">
                        {selectedSlots.map((s, i) => (
                            <button
                                key={i}
                                className="lp-booking-chip"
                                onClick={() => removeSlot(i)}
                                type="button"
                            >
                                {s.label} ✕
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <textarea
                className="lp-showing-input lp-showing-notes"
                placeholder="یادداشت (اختیاری)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
            />

            {error && <div className="lp-showing-error">{error}</div>}

            <button
                className="lp-showing-btn"
                onClick={handleSubmit}
                disabled={submitting || selectedSlots.length === 0}
                type="button"
            >
                {submitting ? 'در حال ارسال...' : 'ثبت درخواست بازدید'}
            </button>
        </div>
    );
}

export default function ListingProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeImage, setActiveImage] = useState(0);

    useEffect(() => {
        api.get(apiRoutes.listings.publicDetail(id))
            .then(res => { setListing(res.data); setLoading(false); })
            .catch(err => { setError(err.response?.data?.detail || err.message); setLoading(false); });
    }, [id]);

    if (loading) return <div className="lp-loading">در حال بارگذاری...</div>;
    if (error) return <div className="lp-error">خطا: {error}</div>;
    if (!listing) return null;

    const prop = listing.property || {};
    const details = listing.details || {};
    const images = listing.images || [];
    const amenities = prop.amenities || [];
    const geoFeatures = prop.geo_features || [];
    const schedules = listing.visiting_schedules || [];
    const isRent = listing.listing_type === 'RENT';
    const isSale = listing.listing_type === 'SALE';

    return (
        <div className="lp-root" dir="rtl">
            {/* Header bar */}
            <div className="lp-header">
                <button className="lp-back-btn" onClick={() => window.close()}>✕ بستن</button>
                <span className="lp-header-title">
                    {prop.name || 'جزئیات آگهی'} — {isRent ? 'رهن و اجاره' : 'فروش'}
                </span>
            </div>

            <div className="lp-content">
                {/* Image gallery */}
                <div className="lp-gallery">
                    {images.length > 0 ? (
                        <>
                            <img
                                className="lp-main-image"
                                src={images[activeImage]?.url}
                                alt={images[activeImage]?.alt_text || 'تصویر ملک'}
                            />
                            {images.length > 1 && (
                                <div className="lp-thumbnails">
                                    {images.map((img, i) => (
                                        <img
                                            key={i}
                                            className={`lp-thumb ${i === activeImage ? 'active' : ''}`}
                                            src={img.url}
                                            alt={img.alt_text || ''}
                                            onClick={() => setActiveImage(i)}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="lp-no-image">تصویری موجود نیست</div>
                    )}
                </div>

                {/* Main info columns */}
                <div className="lp-columns">
                    {/* Left column */}
                    <div className="lp-col">

                        {/* Price section */}
                        <Section title={isRent ? 'اطلاعات رهن و اجاره' : 'اطلاعات فروش'}>
                            {isRent && (
                                <>
                                    <Row label="اجاره ماهانه" value={details.monthly_rent ? `${Number(details.monthly_rent).toLocaleString()} ${listing.currency}` : null} />
                                    <Row label="حداقل ودیعه" value={details.min_deposit ? `${Number(details.min_deposit).toLocaleString()} ${listing.currency}` : null} />
                                    <Row label="حداقل اجاره ماهانه" value={details.minRent ? `${Number(details.minRent).toLocaleString()} ${listing.currency}` : null} />
                                    <Row label="مدت قرارداد" value={details.lease_term_months ? `${details.lease_term_months} ماه` : null} />
                                    <Row label="وضعیت مبله" value={FURNISHED_LABELS[details.furnished_status]} />
                                    <Row label="مناسب برای" value={details.suitable_tenants?.join('، ')} />
                                    <Row label="قیمت منصفانه (ML)" value={details.fair_price ? `${Number(details.fair_price).toLocaleString()} ${listing.currency}` : null} />
                                </>
                            )}
                            {isSale && (
                                <>
                                    <Row label="قیمت درخواستی" value={details.asking_price ? `${Number(details.asking_price).toLocaleString()} ${listing.currency}` : null} />
                                    <Row label="قیمت منصفانه (ML)" value={details.fair_price ? `${Number(details.fair_price).toLocaleString()} ${listing.currency}` : null} />
                                    <Row label="وضعیت وام" value={MORTGAGE_LABELS[details.mortgage_status]} />
                                    {details.mortgage_status === 'AVAILABLE' && (
                                        <>
                                            <Row label="مبلغ کل وام" value={details.mortgage_total_amount ? `${Number(details.mortgage_total_amount).toLocaleString()} ${listing.currency}` : null} />
                                            <Row label="مدت بازپرداخت" value={details.mortgage_repay_period ? `${details.mortgage_repay_period} ماه` : null} />
                                            <Row label="قسط ماهانه" value={details.mortgage_pay_per_month ? `${Number(details.mortgage_pay_per_month).toLocaleString()} ${listing.currency}` : null} />
                                        </>
                                    )}
                                    <Row label="مستأجر دارد" value={details.has_tenant ? 'بله' : 'خیر'} />
                                    {details.has_tenant && (
                                        <>
                                            <Row label="ودیعه مستأجر" value={details.tenant_deposit ? `${Number(details.tenant_deposit).toLocaleString()} ${listing.currency}` : null} />
                                            <Row label="اجاره مستأجر" value={details.tenant_monthly_rent ? `${Number(details.tenant_monthly_rent).toLocaleString()} ${listing.currency}` : null} />
                                        </>
                                    )}
                                </>
                            )}
                            <Row label="در دسترس از" value={listing.available_from} />
                        </Section>

                        {/* Property details */}
                        <Section title="مشخصات ملک">
                            <Row label="نام" value={prop.name} />
                            <Row label="متراژ" value={prop.area ? `${prop.area} متر مربع` : null} />
                            <Row label="سال ساخت" value={prop.establish_date} />
                            <Row label="نوع ملک" value={prop.property_type} />
                            <Row label="کاربری" value={prop.usage} />
                            <Row label="نوع سند" value={prop.deed_type !== 'NONE' ? prop.deed_type : null} />
                            <Row label="توضیحات" value={prop.description} />
                        </Section>

                        {/* Location */}
                        <Section title="موقعیت مکانی">
                            <Row label="استان" value={prop.province} />
                            <Row label="شهر" value={prop.city} />
                            <Row label="منطقه" value={prop.region} />
                            <Row label="محله" value={prop.neighborhood} />
                            <Row label="نشانی (اجمالی)" value={prop.address_hint} />
                            <Row label="کد پستی" value={prop.postal_code} />
                        </Section>
                    </div>

                    {/* Right column */}
                    <div className="lp-col">

                        {/* Amenities */}
                        {amenities.length > 0 && (
                            <Section title="امکانات">
                                <div className="lp-amenities-grid">
                                    {amenities.map(a => (
                                        <div key={a.id} className="lp-amenity-item">
                                            {a.icon && <span className="lp-amenity-icon">{a.icon}</span>}
                                            <span className="lp-amenity-name">{a.description || a.name}</span>
                                            {a.value_type !== 'boolean' && a.value !== null && (
                                                <span className="lp-amenity-value">: {String(a.value)}</span>
                                            )}
                                            {a.value_type === 'boolean' && (
                                                <span className={`lp-amenity-bool ${a.value ? 'yes' : 'no'}`}>
                                                    {a.value ? '✓' : '✗'}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {/* Geo features */}
                        {geoFeatures.length > 0 && (
                            <Section title="ویژگی‌های جغرافیایی">
                                {geoFeatures.map(gf => (
                                    <div key={gf.id} className="lp-geo-row">
                                        <span className="lp-label">{gf.description || gf.name}</span>
                                        <div className="lp-geo-bar-wrap">
                                            {gf.score != null && (
                                                <div className="lp-geo-bar">
                                                    <div className="lp-geo-bar-fill" style={{ width: `${Math.min(gf.score, 100)}%` }} />
                                                </div>
                                            )}
                                            <span className="lp-geo-value">
                                                {gf.score != null ? `${gf.score.toFixed(0)}/100` : gf.value}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </Section>
                        )}

                        {/* Showing request — calendar handles visiting windows */}
                        <Section title="درخواست بازدید">
                            <ShowingRequestWidget listingId={listing.listing_id} schedules={schedules} />
                        </Section>

                        {/* Floor plan */}
                        {listing.plan_url && (
                            <Section title="نقشه پلان">
                                <img className="lp-plan-image" src={listing.plan_url} alt="پلان ملک" />
                            </Section>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
